import React from "react";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { InterviewProblem, ProblemDifficulty, ProblemDifficultyNames } from "../../models/problems";
import { blockConfigs } from "../../models/block";
import { BlockImage } from "../block/BlockImage";
import { COLOR_PRIMARY, COLOR_TEXT_MUTED, COLOR_TEXT_PRIMARY } from "../../styles/colors";
import { ProblemHints } from "./ProblemHints";

const DIFFICULTY_COLORS: Record<ProblemDifficulty, { bg: string; fg: string }> = {
  easy: { bg: "#e5fae5", fg: "#2e7d32" },
  medium: { bg: "#fff4e0", fg: "#b26a00" },
  hard: { bg: "#fde8e8", fg: "#c62828" },
};

export const DifficultyChip: React.FC<{ difficulty: ProblemDifficulty }> = ({ difficulty }) => (
  <Chip
    label={ProblemDifficultyNames[difficulty]}
    size="small"
    sx={{
      fontWeight: 700,
      backgroundColor: DIFFICULTY_COLORS[difficulty].bg,
      color: DIFFICULTY_COLORS[difficulty].fg,
    }}
  />
);

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
    <Typography variant="overline" sx={{ color: COLOR_PRIMARY, fontWeight: 700, lineHeight: 1.6 }}>
      {title}
    </Typography>
    {children}
  </Box>
);

const BulletList: React.FC<{ items: string[] }> = ({ items }) => (
  <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
    {items.map((item) => (
      <Typography component="li" variant="body2" key={item} sx={{ color: COLOR_TEXT_PRIMARY, mb: 0.25 }}>
        {item}
      </Typography>
    ))}
  </Box>
);

/** Requirements, scale targets, suggested blocks and hints for a problem. */
export const ProblemDetails: React.FC<{ problem: InterviewProblem }> = ({ problem }) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
    <Section title="Requirements">
      <BulletList items={problem.functionalRequirements} />
    </Section>
    <Section title="Scale">
      <BulletList items={problem.scaleTargets} />
    </Section>
    <Section title="Blocks you may need">
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
        {problem.suggestedBlocks.map((blockType) => (
          <Tooltip key={blockType} title={blockConfigs[blockType].displayName} arrow>
            <Box data-testid="suggested-block" sx={{ display: "flex" }}>
              <BlockImage blockType={blockType} width={28} height={28} />
            </Box>
          </Tooltip>
        ))}
      </Box>
    </Section>
    <Section title="Hints">
      <ProblemHints problemId={problem.id} hints={problem.hints} />
    </Section>
    <Typography variant="caption" sx={{ color: COLOR_TEXT_MUTED }}>
      Traffic for this problem is preconfigured. You can still tweak it from the TPS display.
    </Typography>
  </Box>
);
